import safeStorage from '@github-ui/safe-storage'
import {useCallback, useEffect, useState, type Dispatch, useRef} from 'react'

const safeSessionStorage = safeStorage('sessionStorage')

const USE_SESSION_STORAGE_UPDATE_EVENT_NAME = 'session-storage-update'

class UseSessionStorageUpdateEvent extends Event {
  storageKey: string
  storageValue: unknown | undefined

  constructor(storageKey: string, storageValue: unknown | undefined) {
    super(USE_SESSION_STORAGE_UPDATE_EVENT_NAME)
    this.storageKey = storageKey
    this.storageValue = storageValue
  }
}

declare global {
  interface DocumentEventMap {
    [USE_SESSION_STORAGE_UPDATE_EVENT_NAME]: UseSessionStorageUpdateEvent
  }
}

/**
 * A hook that mirrors state to session storage
 *
 * The fallback state is _not_ written to session storage, so tearing can occur
 * whenever sessionStorage is empty, if different fallback values are used
 * for the same key, however once a value is updated, other consumers
 * of the same storage keys will update
 */
export function useSessionStorage<T>(
  storageKey: string,
  fallbackState: T,
): readonly [T, Dispatch<T | undefined>, Dispatch<T>] {
  // copy fallbackState to a tracked ref, to avoid re-renders if it has unstable identity
  const fallbackStateRef = useRef(fallbackState)
  useEffect(() => {
    fallbackStateRef.current = fallbackState
  })

  const [value, setValue] = useState<T>(() => {
    const itemValue = safeSessionStorage.getItem(storageKey)
    if (itemValue) {
      return JSON.parse(itemValue)
    }
    return fallbackStateRef.current
  })

  const setNextValue: Dispatch<T | undefined> = useCallback(
    nextValue => {
      setValue(nextValue !== undefined ? nextValue : fallbackStateRef.current)

      if (nextValue === undefined) {
        safeSessionStorage.removeItem(storageKey)
      } else {
        safeSessionStorage.setItem(storageKey, JSON.stringify(nextValue))
      }

      document.dispatchEvent(new UseSessionStorageUpdateEvent(storageKey, nextValue))
    },
    [storageKey],
  )

  const updateFallbackValue = useCallback(
    (nextValue: T) => {
      fallbackStateRef.current = nextValue
    },
    [fallbackStateRef],
  )

  /**
   * When we change the value, we emit an event
   *
   * Subscribe to that event, so we can continuously sync
   * the state
   */
  useEffect(() => {
    function handler(event: UseSessionStorageUpdateEvent) {
      if (event.storageKey === storageKey) {
        const storageValue = event.storageValue as T | undefined
        const nextValue: T = storageValue !== undefined ? storageValue : fallbackStateRef.current
        setValue(nextValue)
      }
    }

    document.addEventListener(USE_SESSION_STORAGE_UPDATE_EVENT_NAME, handler)

    /**
     * during setup, it's _possible_ we've diverged, so we'll
     * immediately check for an update
     *
     * This also provides a 'reset' in the event the storageKey was changed
     */
    const itemValue = safeSessionStorage.getItem(storageKey)
    if (itemValue) {
      setValue(JSON.parse(itemValue))
    } else {
      setValue(fallbackStateRef.current)
    }

    return () => {
      document.removeEventListener(USE_SESSION_STORAGE_UPDATE_EVENT_NAME, handler)
    }
  }, [storageKey])

  return [value, setNextValue, updateFallbackValue]
}

function findSessionKeysWithPrefix(prefix: string): string[] {
  const keys = safeSessionStorage.getKeys()
  const keysWithPrefix: string[] = []
  for (const index in keys) {
    if (keys[index]?.startsWith(prefix)) {
      keysWithPrefix.push(keys[index])
    }
  }
  return keysWithPrefix
}

export function clearSessionStorage(storageKeys: string[]) {
  for (const storageKey of storageKeys) {
    safeSessionStorage.removeItem(storageKey)
    document.dispatchEvent(new UseSessionStorageUpdateEvent(storageKey, undefined))
  }
}

export function getSessionStorageItemsForKeyPrefix(prefix: string): unknown[] {
  const values: unknown[] = []
  const keys = findSessionKeysWithPrefix(prefix)
  for (const key of keys) {
    const itemValue = safeSessionStorage.getItem(key)
    if (itemValue != null) {
      values.push(JSON.parse(itemValue))
    }
  }
  return values
}

export function clearSessionStorageByKeyPrefix(prefix: string) {
  const keysToClear = findSessionKeysWithPrefix(prefix)
  clearSessionStorage(keysToClear)
}
